/**
 * Detail view for a single Telephony Account. Surfaces:
 *  - Phone-number inventory under the account (Slice 2)
 *  - "Add phone number" wizard (Slice 2)
 *  - Brand / Campaign / Messaging Service status (Slice 3, placeholder)
 *
 * Routed from the dealership Telephony tab when an admin clicks
 * "Open" on a Telephony Account row.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  CircleSlash,
  DollarSign,
  MessageSquare,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  cn,
} from "@lumika/ui";
import { COUNTRIES, TelephonyE911AddressSchema, US_STATES } from "@lumika/shared";
import type {
  Rooftop,
  TelephonyAccount,
  TelephonyAvailableNumber,
  TelephonyE911AddressDto,
  TelephonyPhoneNumber,
} from "@lumika/shared";
import { adminApi } from "../../api/admin";
import { formatDateTime } from "../../lib/format";
import { A2pWizardDialog } from "./A2pWizardDialog";
import { BRAND_VARIANT } from "./DealershipTelephonyPage";

export function TelephonyAccountDetailPage() {
  const { dealershipId, telephonyAccountId } = useParams();
  const navigate = useNavigate();
  if (!dealershipId || !telephonyAccountId) return null;

  const accountQ = useQuery({
    queryKey: ["admin", "telephony-account", telephonyAccountId],
    queryFn: () => adminApi.telephonyAccount(telephonyAccountId),
  });
  const numbersQ = useQuery({
    queryKey: ["admin", "telephony-account", telephonyAccountId, "numbers"],
    queryFn: () => adminApi.telephonyAccountNumbers(telephonyAccountId),
  });
  const rooftopsQ = useQuery({
    queryKey: ["admin", dealershipId, "rooftops"],
    queryFn: () => adminApi.listRooftops(dealershipId),
  });

  if (accountQ.isLoading) return <PageLoading message="Loading Telephony Account…" />;
  if (accountQ.isError || !accountQ.data) {
    return (
      <PageError
        title="Couldn't load this Telephony Account"
        description={accountQ.error?.message}
        action={
          <Button variant="outline" onClick={() => navigate(`/dealerships/${dealershipId}/telephony`)}>
            Back
          </Button>
        }
      />
    );
  }
  const account = accountQ.data;
  const numbers = numbersQ.data ?? [];
  // Any rooftop of the dealership can hold this account's numbers —
  // rooftops no longer pin a Telephony Account.
  const dealershipRooftops: Rooftop[] = rooftopsQ.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <div>
        <Link
          to={`/dealerships/${dealershipId}/telephony`}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> All Telephony Accounts
        </Link>
      </div>
      <PageHeader
        eyebrow="Telephony Account"
        title={account.displayName}
        description={account.legalBusinessName ?? "Legal name not set yet."}
      />
      <BrandStatusSection account={account} />
      <PhoneNumbersSection
        telephonyAccountId={telephonyAccountId}
        numbers={numbers}
        dealershipRooftops={dealershipRooftops}
        isLoading={numbersQ.isLoading}
        isError={numbersQ.isError}
        rooftopsLoadFailed={rooftopsQ.isError}
        brandStatus={account.brandStatus}
      />
      <UsageSection telephonyAccountId={telephonyAccountId} />
    </div>
  );
}

function UsageSection({ telephonyAccountId }: { telephonyAccountId: string }) {
  const today = new Date();
  const fallbackEnd = today.toISOString().slice(0, 10);
  const fallbackStart = new Date(today.getTime() - 30 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  const range = { start: fallbackStart, end: fallbackEnd };
  const qc = useQueryClient();
  const usageQ = useQuery({
    queryKey: ["admin", "telephony-account", telephonyAccountId, "usage", range.start, range.end],
    queryFn: () => adminApi.telephonyAccountUsage(telephonyAccountId, range),
  });
  const sync = useMutation({
    mutationFn: () => adminApi.telephonyAccountUsageSync(telephonyAccountId, range),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "telephony-account", telephonyAccountId, "usage"],
      });
      toast.success("Usage synced from carrier.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const totals = usageQ.data?.totals;
  return (
    <Section
      title="Usage (last 30 days)"
      description="Daily voice / SMS / MMS aggregated from the carrier. Sync pulls fresh totals; the nightly job keeps this up to date automatically."
      actions={
        <Button variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
          <RefreshCw className="h-4 w-4" /> {sync.isPending ? "Syncing…" : "Sync"}
        </Button>
      }
    >
      {usageQ.isLoading ? (
        <PageLoading message="Loading usage…" />
      ) : usageQ.isError ? (
        <PageError title="Couldn't load usage" description="Try refreshing the page." />
      ) : !totals || usageQ.data?.rows.length === 0 ? (
        <EmptyState
          title="No usage recorded"
          description="Once calls and texts go through the carrier, totals appear here. Click Sync to force a refresh."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-4">
          <Stat label="Voice minutes" value={totals.voiceMinutes.toFixed(1)} />
          <Stat label="SMS segments" value={totals.smsSegments.toFixed(0)} />
          <Stat label="MMS segments" value={totals.mmsSegments.toFixed(0)} />
          <Stat
            label="Cost (USD)"
            value={
              <span className="inline-flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {totals.priceUsd.toFixed(2)}
              </span>
            }
          />
        </div>
      )}
    </Section>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-muted/40 p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function BrandStatusSection({ account }: { account: TelephonyAccount }) {
  const [open, setOpen] = useState(false);
  // Shared with the accounts list page so the same status never shows a
  // different badge color between the two screens.
  const variant = BRAND_VARIANT[account.brandStatus];
  return (
    <Section
      title="SMS brand & campaign"
      description="Approved 10DLC brand + campaign are required before SMS can leave any number on this Telephony Account."
      actions={
        <Button onClick={() => setOpen(true)} variant={account.brandStatus === "NOT_STARTED" ? "default" : "outline"}>
          <MessageSquare className="h-4 w-4" />
          {account.brandStatus === "NOT_STARTED" ? "Set up A2P" : "Open A2P wizard"}
        </Button>
      }
    >
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">Brand</span>
        <Badge variant={variant}>{account.brandStatus}</Badge>
        {account.brandTrustScore !== null && (
          <span className="text-xs text-muted-foreground">
            Trust score {account.brandTrustScore}
          </span>
        )}
      </div>
      {open && (
        <A2pWizardDialog account={account} onClose={() => setOpen(false)} />
      )}
    </Section>
  );
}

function PhoneNumbersSection({
  telephonyAccountId,
  numbers,
  dealershipRooftops,
  isLoading,
  isError,
  rooftopsLoadFailed,
  brandStatus,
}: {
  telephonyAccountId: string;
  numbers: TelephonyPhoneNumber[];
  dealershipRooftops: Rooftop[];
  isLoading: boolean;
  isError: boolean;
  rooftopsLoadFailed: boolean;
  brandStatus: TelephonyAccount["brandStatus"];
}) {
  const [wizardOpen, setWizardOpen] = useState(false);
  return (
    <Section
      title="Phone numbers"
      description="Voice is live the moment a number is purchased. SMS stays disabled until this Telephony Account's brand and campaign are approved."
      actions={
        <Button onClick={() => setWizardOpen(true)}>
          <Plus className="h-4 w-4" /> Add phone number
        </Button>
      }
    >
      {rooftopsLoadFailed && (
        <p className="mb-3 text-xs text-destructive">
          Couldn't load rooftops — rooftop assignment is temporarily unavailable.
        </p>
      )}
      {isLoading ? (
        <PageLoading message="Loading numbers…" />
      ) : isError ? (
        <PageError title="Couldn't load phone numbers" description="Try refreshing the page." />
      ) : numbers.length === 0 ? (
        <EmptyState
          title="No phone numbers yet"
          description="Purchase a number to enable voice calling on rooftops assigned to this Telephony Account."
        />
      ) : (
        <NumbersTable
          telephonyAccountId={telephonyAccountId}
          rows={numbers}
          dealershipRooftops={dealershipRooftops}
          rooftopsLoadFailed={rooftopsLoadFailed}
          brandStatus={brandStatus}
        />
      )}
      {wizardOpen && (
        <PurchaseWizard
          telephonyAccountId={telephonyAccountId}
          dealershipRooftops={dealershipRooftops}
          rooftopsLoadFailed={rooftopsLoadFailed}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </Section>
  );
}

function NumbersTable({
  telephonyAccountId,
  rows,
  dealershipRooftops,
  rooftopsLoadFailed,
  brandStatus,
}: {
  telephonyAccountId: string;
  rows: TelephonyPhoneNumber[];
  dealershipRooftops: Rooftop[];
  rooftopsLoadFailed: boolean;
  brandStatus: TelephonyAccount["brandStatus"];
}) {
  return (
    <>
      {/* Desktop: full numbers table. */}
      <TableBleed className={cn("hidden overflow-hidden rounded-b-lg md:block", rooftopsLoadFailed ? "-mb-6 border-t" : "-my-6")}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Voice</TableHead>
              <TableHead>SMS</TableHead>
              <TableHead>Rooftop</TableHead>
              <TableHead>Purchased</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((n) => (
              <NumberRow
                key={n.id}
                row={n}
                telephonyAccountId={telephonyAccountId}
                dealershipRooftops={dealershipRooftops}
                rooftopsLoadFailed={rooftopsLoadFailed}
                brandStatus={brandStatus}
              />
            ))}
          </TableBody>
        </Table>
      </TableBleed>
      {/* Mobile: touch list with the same per-number controls. */}
      <MobileList>
        {rows.map((n) => (
          <NumberMobileCard
            key={n.id}
            row={n}
            telephonyAccountId={telephonyAccountId}
            dealershipRooftops={dealershipRooftops}
            rooftopsLoadFailed={rooftopsLoadFailed}
            brandStatus={brandStatus}
          />
        ))}
      </MobileList>
    </>
  );
}

function NumberMobileCard({
  row,
  telephonyAccountId,
  dealershipRooftops,
  rooftopsLoadFailed,
  brandStatus,
}: {
  row: TelephonyPhoneNumber;
  telephonyAccountId: string;
  dealershipRooftops: Rooftop[];
  rooftopsLoadFailed: boolean;
  brandStatus: TelephonyAccount["brandStatus"];
}) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["admin", "telephony-account", telephonyAccountId, "numbers"],
    });
  const reassign = useMutation({
    mutationFn: (rooftopId: string | null) =>
      adminApi.telephonyUpdateNumber(row.id, { rooftopId }),
    onSuccess: () => {
      invalidate();
      toast.success("Rooftop assignment updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const smsEnable = useMutation({
    mutationFn: () => adminApi.telephonySmsEnable(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("SMS enabled — number is in the sender pool.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const smsDisable = useMutation({
    mutationFn: () => adminApi.telephonySmsDisable(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("SMS disabled — number removed from sender pool.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const release = useMutation({
    mutationFn: () => adminApi.telephonyReleaseNumber(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("Number released");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onRelease = () => {
    const ok = window.confirm(
      `Release ${row.phoneNumberE164}?\n\nThis hands the number back to the carrier. Inbound calls and texts will stop immediately. The number may not be reachable again on the same account. This cannot be undone.`,
    );
    if (ok) release.mutate();
  };

  return (
    <MobileListRow
      primary={row.phoneNumberE164}
      secondary={row.phoneNumberFriendly || undefined}
      tertiary={`Purchased ${row.purchasedAt ? formatDateTime(row.purchasedAt) : "—"}`}
      meta={
        <>
          {row.voiceEnabled ? (
            <Badge variant="success" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Voice
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <CircleSlash className="h-3 w-3" /> Disabled
            </Badge>
          )}
          {row.smsEnabled ? (
            <button
              type="button"
              onClick={() => smsDisable.mutate()}
              disabled={smsDisable.isPending}
              aria-label="Disable SMS"
            >
              <Badge variant="success" className="gap-1 cursor-pointer">
                <CheckCircle2 className="h-3 w-3" /> SMS
              </Badge>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => smsEnable.mutate()}
              disabled={smsEnable.isPending}
              aria-label="Enable SMS"
              title={
                brandStatus === "APPROVED"
                  ? "SMS is off for this number. Click to add it back to the sender pool."
                  : "SMS is on hold until the 10DLC campaign is approved. Voice is active and ready in the meantime."
              }
            >
              <Badge variant="warning" className="cursor-pointer">
                {smsEnable.isPending
                  ? "Enabling…"
                  : brandStatus === "APPROVED"
                    ? "SMS off"
                    : "SMS — pending 10DLC"}
              </Badge>
            </button>
          )}
          <div className="w-full">
            <Select
              value={row.rooftopId ?? "__pool__"}
              onValueChange={(v) => reassign.mutate(v === "__pool__" ? null : v)}
              disabled={rooftopsLoadFailed}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__pool__">Pool (unassigned)</SelectItem>
                {dealershipRooftops.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      }
      trailing={
        <Button
          variant="ghost"
          size="sm"
          onClick={onRelease}
          disabled={release.isPending}
          aria-label="Release number"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      }
    />
  );
}

function NumberRow({
  row,
  telephonyAccountId,
  dealershipRooftops,
  rooftopsLoadFailed,
  brandStatus,
}: {
  row: TelephonyPhoneNumber;
  telephonyAccountId: string;
  dealershipRooftops: Rooftop[];
  rooftopsLoadFailed: boolean;
  brandStatus: TelephonyAccount["brandStatus"];
}) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["admin", "telephony-account", telephonyAccountId, "numbers"],
    });
  const reassign = useMutation({
    mutationFn: (rooftopId: string | null) =>
      adminApi.telephonyUpdateNumber(row.id, { rooftopId }),
    onSuccess: () => {
      invalidate();
      toast.success("Rooftop assignment updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const smsEnable = useMutation({
    mutationFn: () => adminApi.telephonySmsEnable(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("SMS enabled — number is in the sender pool.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const smsDisable = useMutation({
    mutationFn: () => adminApi.telephonySmsDisable(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("SMS disabled — number removed from sender pool.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const release = useMutation({
    mutationFn: () => adminApi.telephonyReleaseNumber(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("Number released");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onRelease = () => {
    const ok = window.confirm(
      `Release ${row.phoneNumberE164}?\n\nThis hands the number back to the carrier. Inbound calls and texts will stop immediately. The number may not be reachable again on the same account. This cannot be undone.`,
    );
    if (ok) release.mutate();
  };

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium tabular-nums">{row.phoneNumberE164}</div>
        {row.phoneNumberFriendly && (
          <div className="text-xs text-muted-foreground">{row.phoneNumberFriendly}</div>
        )}
      </TableCell>
      <TableCell>
        {row.voiceEnabled ? (
          <Badge variant="success" className="gap-1">
            <CheckCircle2 className="h-3 w-3" /> Voice
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1">
            <CircleSlash className="h-3 w-3" /> Disabled
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {row.smsEnabled ? (
          <button
            type="button"
            onClick={() => smsDisable.mutate()}
            disabled={smsDisable.isPending}
            aria-label="Disable SMS"
          >
            <Badge variant="success" className="gap-1 cursor-pointer">
              <CheckCircle2 className="h-3 w-3" /> SMS
            </Badge>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => smsEnable.mutate()}
            disabled={smsEnable.isPending}
            aria-label="Enable SMS"
            title={
              brandStatus === "APPROVED"
                ? "SMS is off for this number. Click to add it back to the sender pool."
                : "SMS is on hold until the 10DLC campaign is approved. Voice is active and ready in the meantime."
            }
          >
            <Badge variant="warning" className="cursor-pointer">
              {smsEnable.isPending
                ? "Enabling…"
                : brandStatus === "APPROVED"
                  ? "SMS off"
                  : "SMS — pending 10DLC"}
            </Badge>
          </button>
        )}
      </TableCell>
      <TableCell className="min-w-[180px]">
        <Select
          value={row.rooftopId ?? "__pool__"}
          onValueChange={(v) => reassign.mutate(v === "__pool__" ? null : v)}
          disabled={rooftopsLoadFailed}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__pool__">Pool (unassigned)</SelectItem>
            {dealershipRooftops.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {row.purchasedAt ? formatDateTime(row.purchasedAt) : "—"}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRelease}
          disabled={release.isPending}
          aria-label="Release number"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

type WizardStep = "search" | "address";

// The server's TelephonyE911AddressSchema only accepts a US state +
// ZIP, so the E911 country picker offers US alone. (The number-search
// country picker above stays unrestricted on purpose.)
const E911_COUNTRIES = COUNTRIES.filter((c) => c.code === "US");

function PurchaseWizard({
  telephonyAccountId,
  dealershipRooftops,
  rooftopsLoadFailed,
  onClose,
}: {
  telephonyAccountId: string;
  dealershipRooftops: Rooftop[];
  rooftopsLoadFailed: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // The purchase route is dealership-scoped (the number lands on this
  // dealership's association with the account).
  const { dealershipId } = useParams();
  const [step, setStep] = useState<WizardStep>("search");
  const [search, setSearch] = useState({
    countryCode: "US",
    areaCode: "",
    contains: "",
  });
  const [results, setResults] = useState<TelephonyAvailableNumber[] | null>(null);
  const [picked, setPicked] = useState<TelephonyAvailableNumber | null>(null);
  const [rooftopId, setRooftopId] = useState<string | "__pool__">("__pool__");
  const [address, setAddress] = useState({
    friendlyName: "",
    customerName: "",
    street: "",
    city: "",
    region: "",
    postalCode: "",
    isoCountry: "US",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof address, string>>>({});
  const setAddrField = (key: keyof typeof address, v: string) => {
    setAddress((a) => ({ ...a, [key]: v }));
    // Clear a field's error as soon as the operator starts fixing it.
    setErrors((cur) => (cur[key] ? { ...cur, [key]: undefined } : cur));
  };

  const seedAddressFromRooftop = (id: string) => {
    const r = dealershipRooftops.find((x) => x.id === id);
    if (!r) return;
    setAddress((cur) => ({
      ...cur,
      friendlyName: cur.friendlyName || r.name,
      customerName: cur.customerName || r.name,
      street: cur.street || r.addressLine1 || "",
      city: cur.city || r.city || "",
      region: cur.region || r.state || "",
      postalCode: cur.postalCode || r.zip || "",
    }));
  };

  const searchMut = useMutation({
    mutationFn: () =>
      adminApi.telephonySearchNumbers(telephonyAccountId, {
        countryCode: search.countryCode || "US",
        areaCode: search.areaCode || undefined,
        contains: search.contains || undefined,
        numberType: "LOCAL",
        limit: 10,
      }),
    onSuccess: (data) => {
      // Fresh results invalidate any previous pick — otherwise Continue
      // would still purchase a number from the old result set.
      setResults(data);
      setPicked(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const purchaseMut = useMutation({
    mutationFn: (emergencyAddress: TelephonyE911AddressDto) => {
      if (!picked) throw new Error("Pick a number first.");
      if (!dealershipId) throw new Error("Missing dealership context.");
      return adminApi.telephonyPurchaseNumber(dealershipId, telephonyAccountId, {
        phoneNumber: picked.phoneNumber,
        rooftopId: rooftopId === "__pool__" ? null : rooftopId,
        emergencyAddress,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "telephony-account", telephonyAccountId, "numbers"],
      });
      toast.success(`${picked!.phoneNumber} is live.`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Validate against the SAME schema the backend enforces
  // (TelephonyE911AddressSchema), on click — mirrors the A2P wizard's
  // BrandStep — so the operator sees which field blocks the purchase
  // instead of a silently disabled button.
  const validate = (): TelephonyE911AddressDto | null => {
    const result = TelephonyE911AddressSchema.safeParse(address);
    if (!result.success) {
      const next: Partial<Record<keyof typeof address, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof typeof address | undefined;
        // Every min() in the schema is min(1), so too_small = empty.
        if (key && !next[key]) {
          next[key] = issue.code === "too_small" ? "Required." : issue.message;
        }
      }
      setErrors(next);
      return null;
    }
    setErrors({});
    return result.data;
  };

  // Block backdrop-click / Escape dismissal once the wizard holds work
  // worth keeping (a search, a pick, or the 911-address step).
  const isDirty =
    step !== "search" ||
    results !== null ||
    picked !== null ||
    search.areaCode !== "" ||
    search.contains !== "";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>
            {step === "search" ? "Find a phone number" : "Register the 911 address"}
          </DialogTitle>
        </DialogHeader>
        {/* Scrollable body so the sticky DialogFooter stays pinned
            (esp. on mobile) when the results table grows. */}
        <div className="-mx-5 flex-1 min-h-0 overflow-y-auto px-5 sm:-mx-6 sm:px-6">
          {step === "search" ? (
            <div className="grid gap-4">
              <p className="text-xs text-muted-foreground">
                Search the carrier's available-number inventory. SMS stays disabled on every
                number purchased here until the Telephony Account's brand and campaign are
                approved.
              </p>
              <div className="grid gap-3 md:grid-cols-3">
                <FormField label="Country">
                  <Select
                    value={search.countryCode}
                    onValueChange={(v) => setSearch((s) => ({ ...s, countryCode: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Area code">
                  <Input
                    value={search.areaCode}
                    onChange={(e) =>
                      setSearch((s) => ({ ...s, areaCode: e.target.value.replace(/\D/g, "") }))
                    }
                    placeholder="415"
                    maxLength={3}
                  />
                </FormField>
                <FormField label="Contains">
                  <Input
                    value={search.contains}
                    onChange={(e) => setSearch((s) => ({ ...s, contains: e.target.value }))}
                    placeholder="any digits"
                  />
                </FormField>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => searchMut.mutate()} disabled={searchMut.isPending}>
                  {searchMut.isPending ? "Searching…" : "Search"}
                </Button>
              </div>
              {results && (
                <div className="rounded-md border border-border">
                  {results.length === 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Loosen the filters and try again."
                    />
                  ) : (
                    <>
                      {/* Desktop: pickable results table. */}
                      <div className="hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead></TableHead>
                              <TableHead>Number</TableHead>
                              <TableHead>Location</TableHead>
                              <TableHead>SMS</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {results.map((r) => (
                              <TableRow
                                key={r.phoneNumber}
                                className="cursor-pointer"
                                onClick={() => setPicked(r)}
                              >
                                <TableCell>
                                  <input
                                    type="radio"
                                    name="pick"
                                    aria-label={`Select ${r.phoneNumber}`}
                                    checked={picked?.phoneNumber === r.phoneNumber}
                                    onChange={() => setPicked(r)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium tabular-nums">
                                  {r.phoneNumber}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {[r.locality, r.region].filter(Boolean).join(", ") || "—"}
                                </TableCell>
                                <TableCell>
                                  {r.capabilities.sms ? (
                                    <Badge variant="secondary">SMS-capable</Badge>
                                  ) : (
                                    <Badge variant="warning">Voice only</Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {/* Mobile: tap a card to pick the number. */}
                      <MobileList>
                        {results.map((r) => (
                          <MobileListRow
                            key={r.phoneNumber}
                            onClick={() => setPicked(r)}
                            primary={r.phoneNumber}
                            secondary={
                              [r.locality, r.region].filter(Boolean).join(", ") || "—"
                            }
                            meta={
                              r.capabilities.sms ? (
                                <Badge variant="secondary">SMS-capable</Badge>
                              ) : (
                                <Badge variant="warning">Voice only</Badge>
                              )
                            }
                            trailing={
                              picked?.phoneNumber === r.phoneNumber ? (
                                <Badge variant="success">Selected</Badge>
                              ) : undefined
                            }
                          />
                        ))}
                      </MobileList>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              <p className="text-xs text-muted-foreground">
                The 911 address is registered with the carrier and shown to emergency
                responders if a 911 call originates from this number.
              </p>
              <FormField label="Rooftop (optional)" description="Pre-fills the address from this rooftop.">
                <Select
                  value={rooftopId}
                  onValueChange={(v) => {
                    setRooftopId(v as string);
                    if (v !== "__pool__") seedAddressFromRooftop(v as string);
                  }}
                  disabled={rooftopsLoadFailed}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__pool__">Leave in pool (unassigned)</SelectItem>
                    {dealershipRooftops.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Friendly name" required error={errors.friendlyName}>
                <Input
                  value={address.friendlyName}
                  onChange={(e) => setAddrField("friendlyName", e.target.value)}
                />
              </FormField>
              <FormField label="Business / contact name" required error={errors.customerName}>
                <Input
                  value={address.customerName}
                  onChange={(e) => setAddrField("customerName", e.target.value)}
                />
              </FormField>
              <FormField label="Street" required error={errors.street}>
                <Input
                  value={address.street}
                  onChange={(e) => setAddrField("street", e.target.value)}
                />
              </FormField>
              <div className="grid gap-4 md:grid-cols-4">
                <FormField label="City" required error={errors.city}>
                  <Input
                    value={address.city}
                    onChange={(e) => setAddrField("city", e.target.value)}
                  />
                </FormField>
                <FormField label="State" required error={errors.region}>
                  <Select
                    value={address.region}
                    onValueChange={(v) => setAddrField("region", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a state…" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* A rooftop seed can carry a non-US code — keep it
                          selectable so the trigger isn't blank; validation
                          flags it on Purchase. */}
                      {address.region &&
                        !US_STATES.some((s) => s.code === address.region) && (
                          <SelectItem value={address.region}>{address.region}</SelectItem>
                        )}
                      {US_STATES.map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.code} — {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="ZIP" required error={errors.postalCode}>
                  <Input
                    value={address.postalCode}
                    onChange={(e) => setAddrField("postalCode", e.target.value)}
                  />
                </FormField>
                <FormField label="Country">
                  <Select
                    value={address.isoCountry}
                    onValueChange={(v) => setAddrField("isoCountry", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {E911_COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          {step === "search" ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => setStep("address")} disabled={!picked}>
                Continue
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep("search")}>
                Back
              </Button>
              <Button
                onClick={() => {
                  const dto = validate();
                  if (dto) purchaseMut.mutate(dto);
                }}
                disabled={!picked || purchaseMut.isPending}
              >
                {purchaseMut.isPending ? "Purchasing…" : `Purchase ${picked?.phoneNumber ?? ""}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
